// Client-side BIP-39 / BIP-32 / BIP-44 HD wallet derivation + AES encryption.
// Private keys & seed never leave the browser (zero-knowledge).
import { generateMnemonic as genMn, validateMnemonic as valMn, mnemonicToSeedSync } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english";
import { HDKey } from "@scure/bip32";
import { HDNodeWallet, SigningKey, getBytes } from "ethers";
import * as bitcoin from "bitcoinjs-lib";
import { Keypair } from "@solana/web3.js";
import { derivePath } from "ed25519-hd-key";
import bs58check from "bs58check";
import { keccak_256 } from "@noble/hashes/sha3";
import CryptoJS from "crypto-js";
import { Buffer } from "buffer";

const VAULT_KEY = "cw_vault";
const PEPPER = "corewallet::v1::";

export const generateMnemonic = (words = 12) => genMn(wordlist, words === 24 ? 256 : 128);
export const validateMnemonic = (m) => {
  try {
    return valMn((m || "").trim().toLowerCase().replace(/\s+/g, " "), wordlist);
  } catch {
    return false;
  }
};

const toHex = (u8) => Buffer.from(u8).toString("hex");

function tronAddressFromPriv(privateKey) {
  const sk = new SigningKey(privateKey);
  const pub = sk.publicKey; // 0x04 uncompressed
  const body = getBytes(pub).slice(1);
  const h = keccak_256(body);
  const addr = new Uint8Array(21);
  addr[0] = 0x41;
  addr.set(h.slice(-20), 1);
  return bs58check.encode(Buffer.from(addr));
}

// Derive all chain accounts from a mnemonic (kept in memory only)
export function deriveAccounts(mnemonic) {
  const m = mnemonic.trim().toLowerCase().replace(/\s+/g, " ");
  const seed = mnemonicToSeedSync(m);

  // EVM (ETH/BSC/Polygon) — m/44'/60'/0'/0/0
  const evmNode = HDNodeWallet.fromPhrase(m);
  const evm = { address: evmNode.address, privateKey: evmNode.privateKey };

  // Tron — m/44'/195'/0'/0/0
  const tronNode = HDNodeWallet.fromPhrase(m, "", "m/44'/195'/0'/0/0");
  const tron = { address: tronAddressFromPriv(tronNode.privateKey), privateKey: tronNode.privateKey };

  // Bitcoin — native segwit m/84'/0'/0'/0/0
  const root = HDKey.fromMasterSeed(seed);
  const btcChild = root.derive("m/84'/0'/0'/0/0");
  const btcPub = Buffer.from(btcChild.publicKey);
  const { address: btcAddr } = bitcoin.payments.p2wpkh({ pubkey: btcPub, network: bitcoin.networks.bitcoin });
  const btc = { address: btcAddr, privateKey: toHex(btcChild.privateKey), publicKey: toHex(btcChild.publicKey) };

  // Solana — m/44'/501'/0'/0'
  const solDerived = derivePath("m/44'/501'/0'/0'", Buffer.from(seed).toString("hex")).key;
  const kp = Keypair.fromSeed(solDerived);
  const sol = { address: kp.publicKey.toBase58(), secretKey: Array.from(kp.secretKey) };

  return { mnemonic: m, evm, tron, btc, sol };
}

// AES-256 encrypt mnemonic with PIN
export function encryptVault(mnemonic, pin, wordCount) {
  const ct = CryptoJS.AES.encrypt(mnemonic, PEPPER + pin).toString();
  const vault = { ct, wordCount, createdAt: Date.now() };
  localStorage.setItem(VAULT_KEY, JSON.stringify(vault));
  return vault;
}

export function decryptVault(pin) {
  const raw = localStorage.getItem(VAULT_KEY);
  if (!raw) return null;
  try {
    const { ct } = JSON.parse(raw);
    const bytes = CryptoJS.AES.decrypt(ct, PEPPER + pin);
    const mnemonic = bytes.toString(CryptoJS.enc.Utf8);
    if (!mnemonic || !validateMnemonic(mnemonic)) return null;
    return mnemonic;
  } catch {
    return null;
  }
}

export const hasVault = () => !!localStorage.getItem(VAULT_KEY);
export const clearVault = () => {
  localStorage.removeItem(VAULT_KEY);
  localStorage.removeItem("cw_txs");
};
